import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  DraftEntriesPageDocument,
  LegalEntityLedgerAccountPageFragment,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'

import JournalDraftEntryDetailSidepanel from 'modules/finance/components/JournalDraftEntryDetailSidepanel'
import JournalEntryDetailSidepanel from 'modules/finance/components/JournalEntryDetailSidepanel'
import JournalDraftEntryDatatable from 'modules/finance/components/JournalsDraftEntry/Datatable'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const DraftEntriesPage = () => {
  const [selectedJournalEntryId, setSelectedJournalEntryId] = useState<number | null>(null)
  const [selectedJournalDraftEntryId, setSelectedJournalDraftEntryId] = useState<number | null>(
    null
  )
  const [legalEntity, setLegalEntity] = useState<LegalEntityLedgerAccountPageFragment>()
  const { company, employee } = useCurrentSession()

  const { error, data, loading } = useQuery(DraftEntriesPageDocument)

  const canManageJournalEntries = data?.finance?._policies?.journalEntriesManage?.can ?? false

  const legalEntities = useUnsafeMemo(() => data?.companies?.legalEntities ?? [], [data])

  const legalEntitiesOptions =
    legalEntities.map((le) => ({ label: le.legalName ?? '', value: le.id })) ?? []

  const getDefaultLegalEntityId = useUnsafeMemo(() => {
    if (data && data.companies.legalEntities.length > 0) {
      let preferedLegalEntity = data.companies.legalEntities.find(
        (el) => el.id === employee?.legalEntity?.id
      )
      if (!preferedLegalEntity) {
        preferedLegalEntity = data.companies.legalEntities.find((el) => el.main)
      }
      return preferedLegalEntity?.id
    }
  }, [data, employee?.legalEntity?.id])

  useUnsafeEffect(() => {
    if (getDefaultLegalEntityId) {
      setLegalEntity(legalEntities.find((le) => le.id === getDefaultLegalEntityId))
    }
  }, [getDefaultLegalEntityId, legalEntities, loading])

  const onLegalEntityChange = useUnsafeCallback(
    (value: Record<string, unknown>) => {
      setLegalEntity(legalEntities.find((le) => le.id === value.legalEntityId))
    },
    [legalEntities]
  )

  return (
    <ResponsivePage.Body authorized={canManageJournalEntries} error={error} loading={loading}>
      <Box paddingTop="s32">
        <JournalDraftEntryDatatable
          legalEntityId={legalEntity?.id}
          companyId={company.id}
          onFilterChange={onLegalEntityChange}
          legalEntitiesOptions={legalEntitiesOptions}
          defaultLegalEntityId={getDefaultLegalEntityId}
          onClickDetail={setSelectedJournalDraftEntryId}
        />
      </Box>
      {selectedJournalEntryId && (
        <JournalEntryDetailSidepanel
          journalEntryId={selectedJournalEntryId}
          onClose={() => {
            setSelectedJournalEntryId(null)
          }}
        />
      )}
      {selectedJournalDraftEntryId && (
        <JournalDraftEntryDetailSidepanel
          journalDraftEntryId={selectedJournalDraftEntryId}
          companyId={company.id}
          onClose={() => {
            setSelectedJournalDraftEntryId(null)
          }}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default DraftEntriesPage
